import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core'
import {
  DynamicFieldType,
  ScopeDynamicFieldSettingModel,
} from '@app/features/scoping/models/scope-dynamic-field-setting.model'
import _, { cloneDeep } from 'lodash'
import { catchError, concatMap } from 'rxjs/operators'
import { finalize, from, map } from 'rxjs'
import { getDynamicFieldDropdownSettingsWithOptions, getFieldValuesAsOptions } from '@app/shared/utils/scopes'

import { ApplicationSetting } from '@app/core/model/enums/application-setting.enum'
import { AuthService } from '@app/core/service/auth.service'
import { Brand } from '@app/features/scope-overview/model/brand.model'
import { CountryCodeService } from '@core/service/country-code.service'
import { Money } from '@app/features/scope-overview/model/money.model'
import { OfficeLocation } from '@core/model/office-location'
import { Privilege } from '@app/core/model/enums/privilege.enum'
import { RatecardVersion } from '@app/features/scope-overview/model/ratecard-version.model'
import { ScopeActions } from '@app/features/scoping/store/actions/scoping.actions'
import { ScopeCustomFieldValueStructure } from '@app/features/scope-overview/model/scope-custom-field-value-structure.model'
import { ScopeDetailsService } from '@app/features/scope-overview/service/scope-details.service'
import { ScopeDynamicFieldSettingService } from '@app/features/scoping/service/scope-dynamic-field-setting.service'
import { ScopeMetadataService } from '@core/service/scope-metadata.service'
import { ScopeVersion } from '@core/model/scope-version'
import { ScopingSelectors } from '@app/features/scoping/store/selectors/scoping.selector'
import { SecondParty } from '@core/model/second-party.model'
import { SecondPartyContact } from '../../model/second-party-contact.model'
import { SowRetainedTeamStats } from '@app/features/scope-overview/model/sow-retained-team-stats.model'
import { Store } from '@ngrx/store'
import { TrafficSystemMetadataFieldValue } from '@app/features/scope-overview/model/traffic-system-metadata-field-value.model'
import { User } from '@core/model/user.model'
import { UserSettingsService } from '@app/features/scope-overview/service/user-settings.service'
import { resolveShowUseRetainedTeamToggle } from '@app/features/scope-overview/utils/resolveShowUseRetainedTeamToggle.util'
import { untilDestroyed } from '@shared/utils/utils'
import { Timeout } from '@app/core/model/enums/timeout.enum'

interface DynamicFieldSetting {
  setting: ScopeDynamicFieldSettingModel
  options: { name: string }[]
}

@Component({
  selector: 'scope-details',
  templateUrl: './scope-details.component.html',
  styleUrls: ['./scope-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScopeDetailsComponent implements OnInit, OnChanges {
  private readonly destroy$

  @Input() currentScope!: ScopeVersion
  @Input() officeLocations!: OfficeLocation[] | null
  @Input() secondParties!: SecondParty[] | null
  @Input() selectedSecondParty!: SecondParty | null
  @Input() selectedOfficeLocation!: any | null
  @Input() customFieldsDefinitionOutdated!: boolean | null
  @Input() sowRetainedTeamStats: SowRetainedTeamStats[]
  @Input() benchmarkRateCards!: RatecardVersion[]
  @Input() xlsxTemplates!: any[] | null
  @Input() isUpdateLoading = false

  @Output() onGetSecondParty: EventEmitter<number> = new EventEmitter<number>()
  @Output() onGetOfficeLocation: EventEmitter<number> = new EventEmitter<number>()
  @Output() onConfirmMetaStructureUpdate: EventEmitter<ScopeVersion> = new EventEmitter<ScopeVersion>()
  @Output() onSaveScope: EventEmitter<ScopeVersion> = new EventEmitter<ScopeVersion>()

  scopeDynamicFieldSettings: ScopeDynamicFieldSettingModel[]
  scopeDynamicFieldTextSettings: ScopeDynamicFieldSettingModel[]
  scopeDynamicFieldDropdownSettings: ScopeDynamicFieldSettingModel[]
  scopeDynamicFieldDropdownExternalSettings: ScopeDynamicFieldSettingModel[]
  scopeDynamicFieldDropdownSettingsWithOptions: DynamicFieldSetting[] = []
  scopeDynamicFieldDropdownExternalSettingsWithOptions: DynamicFieldSetting[] = []
  isExternalDataLoading = false

  selectedBrand?: Brand | null
  selectedKeyContact?: SecondPartyContact
  updateScopeRequest: ScopeVersion
  trafficMetaDataFieldTemp: any = {}
  trafficSystemMetadataSearch$: any = {}
  currencyCode?: string
  maximumStartDate: Date
  minimumEndDate: Date
  showUseRetainedTeamToggle: boolean
  trafficMetadataLookupFields: any[]
  contacts: any[] = []
  locations: OfficeLocation[] = []
  customFieldValueStructure: ScopeCustomFieldValueStructure
  isFormValidFlag = false

  get loggedInUser(): User {
    return this.authService.loggedInUser
  }

  constructor(
    private countryCodeService: CountryCodeService,
    private scopeMetadataService: ScopeMetadataService,
    private scopeDetailsService: ScopeDetailsService,
    private store: Store,
    private authService: AuthService,
    private scopeDynamicFieldSettingService: ScopeDynamicFieldSettingService,
    private userSettingsService: UserSettingsService,
    private cdr: ChangeDetectorRef
  ) {
    this.destroy$ = untilDestroyed()
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      (changes.currentScope || changes.sowRetainedTeamStats) &&
      this.currentScope &&
      this.sowRetainedTeamStats &&
      this.loggedInUser?.company.hasApplicationSetting(ApplicationSetting.SOW__RETAINED_HOURS)
    ) {
      this.showUseRetainedTeamToggle = this.resolveShowUseRetainedTeamToggle(
        this.sowRetainedTeamStats,
        this.currentScope.identity.rateCard.id
      )
    }

    if (changes.currentScope)
      this.customFieldValueStructure = cloneDeep(this.currentScope?.scopeVersionCustomFieldValueStructure)

    if (changes.selectedSecondParty) {
      this.contacts = this.selectedSecondParty?.contacts || []
      this.locations = this.selectedSecondParty?.locations || []
    }
    this.recalcFormValidity()
  }

  ngOnInit() {
    this.initScope()
    this.scopeDetailsService.getOfficeLocations()
    this.scopeDetailsService.getXlsxOutputTemplates()
    this.scopeDetailsService.getSecondParties()
    this.scopeDetailsService.getBenchmarkRateCards()
    this.scopeDetailsService.getDefaultRateCard()
    this.scopeDetailsService.getSecondParty(this.currentScope.identity.secondParty.id)
    if (
      this.loggedInUser?.company.hasApplicationSetting('SOW__RETAINED_HOURS') &&
      this.currentScope.scopeOfWorkVersion
    ) {
      this.scopeDetailsService.getRetainedTeamStats(this.currentScope.scopeOfWorkVersion.identity.id)
    }
    this.scopeDetailsService.getCustomFieldsOutdated(this.currentScope.identity.id)
    this.minimumEndDate = this.currentScope.getMinimumEndDate()
    this.maximumStartDate = this.currentScope.getMaximumStartDate()
    this.initialScopeDynamicFieldSettings()
    this.trafficMetadataLookupFields = this.userSettingsService.getTrafficMetadataFields(this.loggedInUser)
    this.recalcFormValidity()
  }

  initScope() {
    this.updateScopeRequest = cloneDeep(this.currentScope)
    this.currencyCode = this.currentScope.identity.rateCard.currencyCode

    for (const i in this.updateScopeRequest.trafficSystemEntityMetadata) {
      if (this.updateScopeRequest.trafficSystemEntityMetadata[i])
        this.trafficMetaDataFieldTemp[i] = this.updateScopeRequest.trafficSystemEntityMetadata[i].value
    }
  }

  initialScopeDynamicFieldSettings() {
    this.store
      .select(ScopingSelectors.selectDynamicFieldSettings)
      .pipe(this.destroy$())
      .subscribe((scopeDynamicFieldSettings: ScopeDynamicFieldSettingModel[]) => {
        this.scopeDynamicFieldSettings = scopeDynamicFieldSettings
        if (this.scopeDynamicFieldSettings && this.scopeDynamicFieldSettings.length > 0)
          this.filterScopeDynamicFieldSettings()
      })
    this.store.dispatch(ScopeActions.getDynamicFieldSettings())
  }

  getSettingsByType = (type: DynamicFieldType) =>
    this.scopeDynamicFieldSettings.filter((setting) => setting.type === type)

  filterScopeDynamicFieldSettings() {
    this.scopeDynamicFieldTextSettings = this.getSettingsByType(DynamicFieldType.TEXT)
    this.scopeDynamicFieldDropdownSettings = this.getSettingsByType(DynamicFieldType.DROPDOWN)
    this.scopeDynamicFieldDropdownExternalSettings = this.getSettingsByType(DynamicFieldType.DROPDOWN_EXTERNAL)

    this.initialTextDynamicFields()

    this.scopeDynamicFieldDropdownSettingsWithOptions = getDynamicFieldDropdownSettingsWithOptions(
      this.scopeDynamicFieldDropdownSettings
    )
    this.buildScopeDynamicFieldDropdownExternalOptions(this.scopeDynamicFieldDropdownExternalSettings)
  }

  initialTextDynamicFields() {
    this.scopeDynamicFieldTextSettings
      .filter((setting) => setting.value)
      .forEach((setting) => {
        if (!this.updateScopeRequest.dynamicFields[setting.name] && setting.mandatory)
          this.updateScopeRequest.dynamicFields[setting.name] = setting.value
      })
  }

  buildScopeDynamicFieldDropdownExternalOptions(
    scopeDynamicFieldDropdownExternalSettings: ScopeDynamicFieldSettingModel[]
  ): void {
    if (this.isExternalDataLoading) return

    this.isExternalDataLoading = true
    this.scopeDynamicFieldDropdownExternalSettingsWithOptions = []

    from(scopeDynamicFieldDropdownExternalSettings)
      .pipe(
        concatMap((dropdownExternalSetting) =>
          this.scopeDynamicFieldSettingService
            .getExternalSystemResource({
              integrationName: dropdownExternalSetting.apiIntegrationName,
              resourceUri: dropdownExternalSetting.externalSystemResourceUri,
            })
            .pipe(
              map((resp) => ({ resp, dropdownExternalSetting })),
              catchError(() => [{ resp: [], dropdownExternalSetting }])
            )
        ),
        finalize(() => {
          this.isExternalDataLoading = false
          this.cdr.detectChanges()
        })
      )
      .subscribe({
        next: ({ resp, dropdownExternalSetting }) => {
          const options = getFieldValuesAsOptions(resp, dropdownExternalSetting.jsonValueName)
          const existingIndex = this.scopeDynamicFieldDropdownExternalSettingsWithOptions.findIndex(
            (de) => de.setting.name === dropdownExternalSetting.name
          )

          if (existingIndex === -1)
            this.scopeDynamicFieldDropdownExternalSettingsWithOptions.push({
              setting: dropdownExternalSetting,
              options,
            })
          else this.scopeDynamicFieldDropdownExternalSettingsWithOptions[existingIndex].options = options

          this.cdr.detectChanges()
        },
      })
  }

  getSecondPartyById(secondPartyId: number) {
    this.updateScopeRequest.identity.contact = null
    this.updateScopeRequest.identity.location = null
    this.updateScopeRequest.identity.brand = null
    this.selectedSecondParty = null
    this.selectedBrand = null
    setTimeout(() => {
      if (secondPartyId && this.loggedInUser && this.loggedInUser.hasPrivilege(Privilege.SECOND_PARTY__VIEW)) {
        this.onGetSecondParty.emit(secondPartyId)
      } else {
        this.contacts = []
        this.locations = []
      }
    }, Timeout.L)
  }

  onSelectSecondParty(secondParty: SecondParty) {
    this.updateScopeRequest.identity.secondParty = secondParty
    this.getSecondPartyById(secondParty?.id)
    this.recalcFormValidity()
  }

  onSelectBrand(brand: Brand) {
    this.selectedBrand = brand
    this.updateScopeRequest.identity.brand = brand
    if (brand) {
      this.updateScopeRequest.identity.contact = brand.keyContact
      this.updateScopeRequest.identity.location = brand.location
    }
  }

  onSelectKeyContact(contact: SecondPartyContact) {
    this.selectedKeyContact = contact
    this.updateScopeRequest.identity.contact = contact
  }

  onSelectLocation(location: OfficeLocation) {
    this.updateScopeRequest.identity.location = location
  }

  onSelectTemplate(template: any) {
    this.updateScopeRequest.identity.xlsxTemplate = template
  }

  onSelectBenchmarkRateCard(rateCard: RatecardVersion) {
    this.updateScopeRequest.identity.benchmarkRateCard = rateCard
  }

  updateTrafficSystemMetadataField(uuid: any) {
    const val = this.trafficMetaDataFieldTemp[uuid]

    if (this.updateScopeRequest.trafficSystemEntityMetadata == undefined)
      this.updateScopeRequest.trafficSystemEntityMetadata = new Map<string, TrafficSystemMetadataFieldValue>()

    this.updateScopeRequest.trafficSystemEntityMetadata[uuid] = { name: val, value: val }
  }

  onSearchTrafficField(searchString: string, secondPartyId: number, rateCardId: number, uuid: string) {
    this.trafficSystemMetadataSearch$[uuid] = this.scopeMetadataService.callScopeMetadataLookupUrl(
      uuid,
      secondPartyId,
      rateCardId,
      searchString
    )
  }

  getLocationByISO = (iso2: any) => this.countryCodeService.getCountryByISO2(iso2)

  getSecondParties = () =>
    (this.selectedOfficeLocation ? this.selectedOfficeLocation.restrictedSecondParties : this.secondParties) || []

  getContacts = () =>
    this.selectedBrand
      ? this.contacts.filter((contact) => contact.location?.id === this.selectedBrand!.location?.id)
      : this.contacts || []

  getLocations = () =>
    (this.selectedBrand
      ? this.selectedSecondParty?.locations?.filter((location: any) => location.id === this.selectedBrand!.location?.id)
      : this.locations) || []

  saveDetails() {
    if (this.isFormValid()) {
      this.updateScopeRequest.scopeVersionCustomFieldValueStructure = this.customFieldValueStructure
      this.updateScopeRequest.companyScopeCustomFieldsDefinition = this.currentScope.companyScopeCustomFieldsDefinition

      if (!this.currentScope.isDraftOrConfigDraft()) {
        const scopeWithOnlyDynamicFields = cloneDeep(this.currentScope)
        scopeWithOnlyDynamicFields.dynamicFields = this.updateScopeRequest.dynamicFields
        this.onSaveScope.emit(scopeWithOnlyDynamicFields)
      } else {
        this.onSaveScope.emit(cloneDeep(this.updateScopeRequest))
      }
    }
  }

  private recalcFormValidity(): void {
    this.isFormValidFlag = this.isFormValid()
    this.cdr.markForCheck()
  }

  isFormValid() {
    if (!this.currentScope.isDraftOrConfigDraft()) {
      return this.isDynamicFieldsValid()
    }
    if (
      this.updateScopeRequest &&
      this.updateScopeRequest.identity.secondParty &&
      this.updateScopeRequest.identity.secondParty.id !== null
    ) {
      return (
        !_.isEmpty(this.updateScopeRequest.name) &&
        this.updateScopeRequest.budget?.amount >= 0 &&
        this.updateScopeRequest.thirdPartyCostBudget?.amount >= 0
      )
    }

    return false
  }

  confirmMetaStructureUpdate() {
    this.onConfirmMetaStructureUpdate.emit(this.currentScope)
  }

  selectOfficeLocation(location: any) {
    if (location) {
      this.updateScopeRequest.identity.officeLocation = location
      this.onGetOfficeLocation.emit(location.id)
    } else {
      this.updateScopeRequest.identity.officeLocation = null
      this.selectedOfficeLocation = null
      this.selectedSecondParty = null
      this.selectedBrand = null
      this.updateScopeRequest.identity.secondParty = null
      this.updateScopeRequest.identity.brand = null
      this.updateScopeRequest.identity.contact = null
      this.updateScopeRequest.identity.location = null
      this.updateScopeRequest.identity.template = null
    }
  }

  selectTrafficField(field: any, uuid: string) {
    this.updateScopeRequest.trafficSystemEntityMetadata[uuid] = field
  }

  updateBudget(amount: number) {
    this.updateScopeRequest.budget = new Money(amount, this.currencyCode)
    this.recalcFormValidity()
  }

  updateTPCBudget(amount: number) {
    this.updateScopeRequest.thirdPartyCostBudget = new Money(amount, this.currencyCode)
    this.recalcFormValidity()
  }

  updateUseRetainedHours(useRetainedHours: boolean) {
    this.updateScopeRequest.useRetainedHours = useRetainedHours
  }

  onInputDynamicFieldTextSettingChange(setting: ScopeDynamicFieldSettingModel, newValue: any) {
    this.updateScopeRequest.dynamicFields[setting.name] = newValue?.trim() ? newValue : null
    this.recalcFormValidity()
  }

  onSelectDropdownDynamicField(dynamicField: any, name: string) {
    this.updateScopeRequest.dynamicFields[name] = !dynamicField.event ? null : dynamicField.event.name
    this.recalcFormValidity()
  }

  onSelectDropdownExternalDynamicField($event: any, item: any) {
    this.updateScopeRequest.dynamicFields[item.setting.name] =
      $event.event === null ? null : ($event.event.item ?? $event.item)
    this.recalcFormValidity()
  }

  getExternalDropdownDynamicFieldValue = (payload: { setting: ScopeDynamicFieldSettingModel; options: any[] }) =>
    payload.options?.find((option) => {
      const settingName = payload.setting?.name
      const dynamicField = this.currentScope.dynamicFields[settingName]
      const optionId = dynamicField?.Id || dynamicField?.id
      return optionId != null && (option.item.Id === optionId || option.item.id === optionId)
    })

  isDynamicFieldsValid() {
    if (this.isExternalDataLoading) return false
    if (this.scopeDynamicFieldSettings)
      return this.scopeDynamicFieldSettings
        .filter((s) => s.mandatory)
        .every((s) => this.updateScopeRequest.dynamicFields[s.name] != null)

    return true
  }

  getBudgetLabel = () => `Budget (${this.currencyCode})`
  getTPCBudgetLabel = () => `Third Party Cost Budget (${this.currencyCode})`

  getDropdownDynamicFieldValue = (setting) => ({ name: this.updateScopeRequest.dynamicFields[setting.setting?.name] })
  getTextDynamicFieldValue = (setting) => this.updateScopeRequest.dynamicFields[setting.name] || ''
  getContactDisplayName = (item: any): string => item?.contactName || ''
  getLocationSimpleName = (item?: OfficeLocation) => item?.location || ''

  getLocationDisplayName = (item?: OfficeLocation) => {
    if (item) {
      const country = this.getLocationByISO(item.country)
      return `${item.location} (${item.getAddress()} ${country.Country_Name}, ${country.Continent})`
    }

    return ''
  }

  getOfficeLocationDisplayName = (item: any) =>
    item
      ? `${item.name} (${item.countryName}, ${
          item.region.charAt(0).toUpperCase() + item.region.slice(1).toLowerCase()
        })`
      : ''

  getMinEndDate = () =>
    this.minimumEndDate
      ? new Date(
          Math.max(this.updateScopeRequest.startDate?.getTime(), this.currentScope.getMinimumEndDate()?.getTime())
        )
      : this.updateScopeRequest.startDate

  getTrafficFieldOption = (item: any) => (item ? `${item.name} (${item.description})` : '')
  isUseRetainedHours = (): boolean => this.updateScopeRequest.useRetainedHours

  protected readonly Privilege = Privilege
  protected readonly ApplicationSetting = ApplicationSetting
  protected readonly resolveShowUseRetainedTeamToggle = resolveShowUseRetainedTeamToggle
}
